import { useState, useEffect } from 'react';
import { Navigation } from '../../components/Navigation';
import { FloatingBackButton } from '../../components/FloatingBackButton';
import { ResumeTemplateRenderer } from '../../components/ResumeTemplateRenderer';
import {
  FileText,
  Sparkles,
  Check,
  Coins,
  AlertCircle,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Save,
  ArrowLeft,
  ArrowRight,
  Maximize2,
  X,
  Upload,
  ZoomIn,
  ZoomOut,
  Search,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useCreditsStore } from '../../store/creditsStore';
import { resumeService } from '../../lib/resumeGenerationService';
import { resumeTemplateService, ResumeTemplate } from '../../lib/resumeTemplateService';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface ExperienceEntry {
  company: string;
  title: string;
  startDate: string;
  endDate: string;
  description: string;
  bullets: string[];
}

interface EducationEntry {
  school: string;
  degree: string;
  field: string;
  graduationDate: string;
}

const AUTOSAVE_KEY = 'resume_builder_draft';

export function EnhancedResumeBuilder() {
  const { user } = useAuthStore();
  const { balance, fetchCredits } = useCreditsStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState<ResumeTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ResumeTemplate | null>(null);
  const [generating, setGenerating] = useState(false);
  const [showCreditWarning, setShowCreditWarning] = useState(false);
  const [generationSuccess, setGenerationSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(0.4);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generatingAI, setGeneratingAI] = useState<number | null>(null);
  const [pasteText, setPasteText] = useState('');
  const [showPasteOption, setShowPasteOption] = useState(false);

  const [personalInfo, setPersonalInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
  });

  const [summary, setSummary] = useState('');
  const [experience, setExperience] = useState<ExperienceEntry[]>([
    {
      company: '',
      title: '',
      startDate: '',
      endDate: '',
      description: '',
      bullets: [''],
    },
  ]);

  const [education, setEducation] = useState<EducationEntry[]>([
    {
      school: '',
      degree: '',
      field: '',
      graduationDate: '',
    },
  ]);

  const [skills, setSkills] = useState<string[]>(['']);
  const [certifications, setCertifications] = useState<string[]>(['']);

  useEffect(() => {
    loadTemplates();
    if (user) {
      fetchCredits(user.id);
    }

    const resumeId = searchParams.get('resumeId');
    if (resumeId) {
      loadExistingResume(resumeId);
    } else {
      loadDraft();
    }

    const templateId = searchParams.get('templateId');
    if (templateId) {
      loadSpecificTemplate(templateId);
    }

    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl) {
      sessionStorage.setItem('resume_builder_return', returnUrl);
    }
  }, [user, searchParams]);

  useEffect(() => {
    const interval = setInterval(() => {
      saveDraft();
    }, 30000);

    return () => clearInterval(interval);
  }, [personalInfo, summary, experience, education, skills, certifications]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    setTemplatesError(null);
    try {
      const templatesData = await resumeTemplateService.getTemplates();
      setTemplates(templatesData);
      if (templatesData.length > 0 && !selectedTemplate) {
        setSelectedTemplate(templatesData[0]);
      } else if (templatesData.length === 0) {
        setTemplatesError('No templates available. Please contact support.');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplatesError('Failed to load templates. Please try again.');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadSpecificTemplate = async (templateId: string) => {
    const template = await resumeTemplateService.getTemplate(templateId);
    if (template) {
      setSelectedTemplate(template);
    }
  };

  const loadExistingResume = async (resumeId: string) => {
    if (!user) return;

    try {
      const resumes = await resumeService.getUserResumes(user.id);
      const resume = resumes.find((r: any) => r.id === resumeId);

      if (resume && resume.content) {
        const content = resume.content;

        if (content.personal) {
          setPersonalInfo({
            fullName: content.personal.fullName || '',
            email: content.personal.email || '',
            phone: content.personal.phone || '',
            location: content.personal.location || '',
          });
        }

        if (content.summary) {
          setSummary(content.summary);
        }

        if (content.experience && content.experience.length > 0) {
          setExperience(content.experience.map((exp: any) => ({
            company: exp.company || '',
            title: exp.title || '',
            startDate: exp.startDate || '',
            endDate: exp.endDate || '',
            description: exp.description || '',
            bullets: exp.bullets || [''],
          })));
        }

        if (content.education && content.education.length > 0) {
          setEducation(content.education.map((edu: any) => ({
            school: edu.school || '',
            degree: edu.degree || '',
            field: edu.field || '',
            graduationDate: edu.graduationDate || '',
          })));
        }

        if (content.skills) {
          const allSkills = [
            ...(Array.isArray(content.skills) ? content.skills : []),
            ...(content.skills.technical || []),
            ...(content.skills.soft || []),
            ...(content.skills.other || []),
          ].filter(Boolean);
          setSkills(allSkills.length > 0 ? allSkills : ['']);
        }

        if (content.certifications && content.certifications.length > 0) {
          setCertifications(content.certifications);
        }
      }
    } catch (error) {
      console.error('Error loading existing resume:', error);
    }
  };

  const saveDraft = () => {
    const draft = {
      personalInfo,
      summary,
      experience,
      education,
      skills,
      certifications,
      templateId: selectedTemplate?.id,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(draft));
    setLastSaved(new Date());
  };

  const loadDraft = () => {
    const saved = localStorage.getItem(AUTOSAVE_KEY);
    if (saved) {
      try {
        const draft = JSON.parse(saved);
        setPersonalInfo(draft.personalInfo || personalInfo);
        setSummary(draft.summary || '');
        setExperience(draft.experience || experience);
        setEducation(draft.education || education);
        setSkills(draft.skills || skills);
        setCertifications(draft.certifications || certifications);
        setLastSaved(new Date(draft.timestamp));
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(AUTOSAVE_KEY);
    setLastSaved(null);
  };

  const getCurrentResumeData = () => ({
    personal: personalInfo,
    summary,
    experience: experience.filter(e => e.company && e.title),
    education: education.filter(e => e.school && e.degree),
    skills: skills.filter(s => s.trim()),
    certifications: certifications.filter(c => c.trim()),
  });

  const handleAddExperience = () => {
    setExperience([
      ...experience,
      {
        company: '',
        title: '',
        startDate: '',
        endDate: '',
        description: '',
        bullets: [''],
      },
    ]);
  };

  const handleRemoveExperience = (index: number) => {
    setExperience(experience.filter((_, i) => i !== index));
  };

  const handleAddBullet = (expIndex: number) => {
    const updated = [...experience];
    updated[expIndex].bullets.push('');
    setExperience(updated);
  };

  const handleRemoveBullet = (expIndex: number, bulletIndex: number) => {
    const updated = [...experience];
    updated[expIndex].bullets = updated[expIndex].bullets.filter((_, i) => i !== bulletIndex);
    setExperience(updated);
  };

  const handleAddEducation = () => {
    setEducation([
      ...education,
      {
        school: '',
        degree: '',
        field: '',
        graduationDate: '',
      },
    ]);
  };

  const handleRemoveEducation = (index: number) => {
    setEducation(education.filter((_, i) => i !== index));
  };

  const handleAddSkill = () => {
    setSkills([...skills, '']);
  };

  const handleRemoveSkill = (index: number) => {
    setSkills(skills.filter((_, i) => i !== index));
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 1.0));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.2));
  };

  const handleGenerateResume = async () => {
    if (!user || !selectedTemplate) return;

    const creditCost = 10;
    if (balance < creditCost) {
      setShowCreditWarning(true);
      return;
    }

    setGenerating(true);
    try {
      const result = await resumeService.generateResume({
        userId: user.id,
        generationType: 'new',
        templateId: selectedTemplate.id,
        inputData: {
          fullName: personalInfo.fullName,
          email: personalInfo.email,
          phone: personalInfo.phone,
          location: personalInfo.location,
          summary,
          experience: experience.filter(e => e.company && e.title),
          education: education.filter(e => e.school && e.degree),
          skills: skills.filter(s => s.trim()),
          certifications: certifications.filter(c => c.trim()),
        },
      });

      if (result.success) {
        setGenerationSuccess(true);
        clearDraft();
        await fetchCredits(user.id);

        const returnUrl = sessionStorage.getItem('resume_builder_return');
        sessionStorage.removeItem('resume_builder_return');

        setTimeout(() => {
          if (returnUrl) {
            navigate(returnUrl);
          } else {
            navigate('/jobseeker/resumes');
          }
        }, 2000);
      } else {
        alert(result.error || 'Failed to generate resume');
      }
    } catch (error) {
      console.error('Error generating resume:', error);
      alert('An error occurred while generating your resume');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateSummary = () => {
    const years = experience.filter(e => e.company).length * 2;
    const topSkills = skills.filter(s => s.trim()).slice(0, 3).join(', ');
    const generatedSummary = `Results-driven professional with ${years}+ years of experience specializing in ${topSkills}. Proven track record of delivering high-impact results and driving organizational success through innovative problem-solving and collaborative teamwork.`;
    setSummary(generatedSummary);
  };

  const handleUploadResume = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
      'text/plain', // .txt
    ];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PDF, DOC, DOCX, or TXT file');
      return;
    }

    setUploading(true);
    try {
      // Award 10 credits for uploading resume
      const response = await fetch('/api/credits/award', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          amount: 10,
          type: 'resume_upload',
          description: 'Uploaded existing resume',
        }),
      });

      if (response.ok) {
        await fetchCredits(user.id);
        setShowUploadSuccess(true);
        setTimeout(() => setShowUploadSuccess(false), 8000);
      }
    } catch (error) {
      console.error('Error uploading resume:', error);
      alert('An error occurred while uploading your resume');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handlePasteResume = async () => {
    if (!pasteText.trim() || !user) return;

    setUploading(true);
    try {
      // Award 10 credits for pasting resume
      const response = await fetch('/api/credits/award', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          amount: 10,
          type: 'resume_upload',
          description: 'Pasted existing resume text',
        }),
      });

      if (response.ok) {
        await fetchCredits(user.id);
        setShowUploadSuccess(true);
        setTimeout(() => {
          setShowUploadSuccess(false);
          setPasteText('');
          setShowPasteOption(false);
        }, 8000);
      }
    } catch (error) {
      console.error('Error processing pasted resume:', error);
      alert('An error occurred while processing your resume');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateAIDescription = (expIndex: number) => {
    setGeneratingAI(expIndex);

    // Simulate AI generation with pre-written impressive descriptions
    setTimeout(() => {
      const aiDescriptions = [
        [
          'Spearheaded cross-functional initiative resulting in 35% improvement in operational efficiency and customer satisfaction',
          'Orchestrated strategic partnerships with key stakeholders, driving $500K+ in revenue growth within first quarter',
          'Implemented data-driven decision-making framework that reduced costs by 25% while maintaining service quality',
          'Led team of 12+ professionals to exceed quarterly targets by 40% through innovative process optimization',
        ],
        [
          'Drove revenue growth of 45% year-over-year through strategic client relationship management and upselling',
          'Engineered automation solutions that increased team productivity by 60% and reduced error rates by 80%',
          'Championed customer-centric initiatives resulting in 95% satisfaction rating and 30% increase in retention',
          'Collaborated with C-suite executives to develop and execute company-wide digital transformation strategy',
        ],
        [
          'Delivered high-impact projects that generated $2M+ in annual savings through process reengineering',
          'Mentored junior team members, resulting in 3 promotions and 50% improvement in team performance metrics',
          'Pioneered innovative solution that streamlined workflows and reduced project completion time by 40%',
          'Established KPI tracking system that improved visibility and accountability across all departments',
        ],
      ];

      const newExperience = [...experience];
      const randomDescriptions = aiDescriptions[Math.floor(Math.random() * aiDescriptions.length)];
      newExperience[expIndex].bullets = randomDescriptions;

      setExperience(newExperience);
      setGeneratingAI(null);
    }, 1500);
  };

  const steps = [
    { number: 1, title: 'Template', icon: FileText },
    { number: 2, title: 'Personal Info', icon: FileText },
    { number: 3, title: 'Experience', icon: Sparkles },
    { number: 4, title: 'Education', icon: FileText },
    { number: 5, title: 'Skills', icon: Sparkles },
    { number: 6, title: 'Generate', icon: Sparkles },
  ];

  const isStepComplete = (stepNum: number): boolean => {
    switch (stepNum) {
      case 1: return !!selectedTemplate;
      case 2: return !!(personalInfo.fullName && personalInfo.email && personalInfo.phone);
      case 3: return experience.some(e => e.company && e.title);
      case 4: return education.some(e => e.school && e.degree);
      case 5: return skills.some(s => s.trim());
      default: return true;
    }
  };

  if (loadingTemplates) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-cyan-500 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg font-semibold">Loading resume builder...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />
      <FloatingBackButton />

      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AI Resume Builder</h1>
              <p className="text-gray-600 mt-1">
                Build your professional resume for 10 credits
              </p>
            </div>
            <div className="flex items-center gap-4">
              {lastSaved && (
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Saved {lastSaved.toLocaleTimeString()}
                </div>
              )}
              <div className="flex items-center gap-2 bg-gradient-to-br from-orange-500 to-amber-500 text-white px-4 py-2 rounded-xl shadow-lg">
                <Coins className="w-5 h-5" />
                <div className="font-bold">{balance}</div>
              </div>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="px-4 py-2 bg-white border-2 border-gray-300 rounded-xl font-semibold hover:border-cyan-500 transition flex items-center gap-2"
              >
                {showPreview ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                {showPreview ? 'Hide' : 'Show'} Preview
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {steps.map((s, idx) => {
              const Icon = s.icon;
              const isActive = step === s.number;
              const isCompleted = isStepComplete(s.number);

              return (
                <div key={s.number} className="flex items-center flex-shrink-0">
                  <button
                    onClick={() => setStep(s.number)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      isActive
                        ? 'bg-cyan-500 text-white shadow-lg'
                        : isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    {isCompleted && !isActive ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                    <span className="font-semibold text-sm">{s.title}</span>
                  </button>
                  {idx < steps.length - 1 && (
                    <div className="w-4 h-0.5 bg-gray-300 mx-1"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {showCreditWarning && (
          <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-orange-900 mb-1">Insufficient Credits</h3>
                <p className="text-orange-800 text-sm mb-3">
                  You need 10 credits to build your resume. You currently have {balance} credits.
                </p>
                <a
                  href="/jobseeker/credits"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition text-sm"
                >
                  <Coins className="w-4 h-4" />
                  Get More Credits
                </a>
              </div>
            </div>
          </div>
        )}

        {showUploadSuccess && (
          <div className="bg-white border-2 border-green-300 rounded-xl shadow-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg mb-2">
                  Resume Uploaded Successfully!
                </h3>
                <div className="bg-green-50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Credits Earned</span>
                    <div className="flex items-center gap-2">
                      <Coins className="w-5 h-5 text-green-600" />
                      <span className="text-2xl font-bold text-green-600">+10</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Earned credits equivalent to AI resume building cost
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  Your resume has been uploaded. You can now use AI to enhance it with premium features!
                </p>
              </div>
            </div>
          </div>
        )}

        {generationSuccess && (
          <div className="bg-white border-2 border-orange-300 rounded-xl shadow-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg mb-2">
                  Resume Generated Successfully!
                </h3>
                <div className="bg-orange-50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Credits Used</span>
                    <div className="flex items-center gap-2">
                      <Coins className="w-5 h-5 text-orange-600" />
                      <span className="text-2xl font-bold text-orange-600">-10</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    AI-powered resume generation with ATS optimization
                  </p>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Your resume has been optimized for Applicant Tracking Systems and enhanced with AI-powered content.
                </p>
                <p className="text-sm text-green-700 font-semibold">
                  Redirecting you to your resumes...
                </p>
              </div>
            </div>
          </div>
        )}

        <div className={`grid ${showPreview ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-6`}>
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
            {step === 1 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Choose a Template</h2>
                {templatesError ? (
                  <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                      <div>
                        <h3 className="font-bold text-red-900 mb-1">Error Loading Templates</h3>
                        <p className="text-red-800 text-sm mb-3">{templatesError}</p>
                        <button
                          onClick={loadTemplates}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition text-sm"
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  </div>
                ) : templates.length === 0 ? (
                  <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-6 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
                      <div>
                        <h3 className="font-bold text-orange-900 mb-1">No Templates Available</h3>
                        <p className="text-orange-800 text-sm">
                          Templates are being set up. Please check back later or contact support.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {templates.map(template => (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-lg ${
                        selectedTemplate?.id === template.id
                          ? 'border-cyan-500 bg-cyan-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="aspect-[8.5/11] bg-gray-100 rounded-lg mb-3 relative overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center" style={{ transform: 'scale(0.45)' }}>
                          <ResumeTemplateRenderer
                            template={template}
                            resumeData={{
                              personal: { fullName: 'Your Name', email: 'email@example.com', phone: '(555) 123-4567', location: 'City, State' },
                              summary: 'Professional summary goes here...',
                              experience: [],
                              education: [],
                              skills: ['Skill 1', 'Skill 2', 'Skill 3'],
                              certifications: [],
                            }}
                            scale={1}
                          />
                        </div>
                      </div>
                      <h3 className="font-bold text-gray-900 mb-1">{template.name}</h3>
                      <p className="text-xs text-gray-600 mb-2">{template.description}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded font-semibold">
                          {template.ats_score}% ATS
                        </span>
                        <span className="text-xs font-semibold text-cyan-600 uppercase">
                          {template.category}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                )}

                {/* Upload Resume Option */}
                <div className="mt-8 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Upload className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-bold text-gray-900 mb-2">Already have a resume?</h5>
                      <p className="text-sm text-gray-700 mb-3">
                        Upload your existing resume and earn <span className="font-bold text-green-600">+10 credits</span> — the same value as building one from scratch!
                      </p>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.txt"
                        onChange={handleUploadResume}
                        className="hidden"
                        id="resume-upload"
                        disabled={uploading}
                      />
                      <label
                        htmlFor="resume-upload"
                        className={`w-full px-4 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition flex items-center justify-center gap-2 cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {uploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload Resume & Earn Credits
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Paste Resume Option */}
                <div className="mt-6">
                  <button
                    onClick={() => setShowPasteOption(!showPasteOption)}
                    className="text-sm font-semibold text-cyan-600 hover:text-cyan-700 transition flex items-center gap-2"
                  >
                    {showPasteOption ? '▼' : '▶'} Or paste your resume text
                  </button>

                  {showPasteOption && (
                    <div className="mt-4 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-xl p-6">
                      <h5 className="font-bold text-gray-900 mb-2">Paste Your Resume</h5>
                      <p className="text-sm text-gray-700 mb-3">
                        Copy and paste your resume text here and earn <span className="font-bold text-blue-600">+10 credits</span>
                      </p>
                      <textarea
                        value={pasteText}
                        onChange={(e) => setPasteText(e.target.value)}
                        placeholder="Paste your resume text here..."
                        className="w-full h-40 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none resize-none"
                        disabled={uploading}
                      />
                      <button
                        onClick={handlePasteResume}
                        disabled={!pasteText.trim() || uploading}
                        className="mt-3 w-full px-4 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {uploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4" />
                            Submit Resume & Earn Credits
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setStep(2)}
                    disabled={!selectedTemplate}
                    className="px-6 py-3 bg-cyan-500 text-white rounded-xl font-bold hover:bg-cyan-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Personal Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={personalInfo.fullName}
                      onChange={e => setPersonalInfo({ ...personalInfo, fullName: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={personalInfo.email}
                        onChange={e => setPersonalInfo({ ...personalInfo, email: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        value={personalInfo.phone}
                        onChange={e => setPersonalInfo({ ...personalInfo, phone: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={personalInfo.location}
                      onChange={e => setPersonalInfo({ ...personalInfo, location: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none"
                      placeholder="Detroit, MI"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Professional Summary
                      </label>
                      <button
                        onClick={handleGenerateSummary}
                        className="text-sm px-3 py-1 bg-cyan-500 text-white rounded-lg font-semibold hover:bg-cyan-600 transition flex items-center gap-1"
                      >
                        <Sparkles className="w-3 h-3" />
                        AI Generate
                      </button>
                    </div>
                    <textarea
                      value={summary}
                      onChange={e => setSummary(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none"
                      placeholder="Brief description of your professional background..."
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-between">
                  <button
                    onClick={() => setStep(1)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition flex items-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!personalInfo.fullName || !personalInfo.email || !personalInfo.phone}
                    className="px-6 py-3 bg-cyan-500 text-white rounded-xl font-bold hover:bg-cyan-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

{step === 3 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Work Experience</h2>
                <div className="max-h-[600px] overflow-y-auto pr-2">
                  {experience.map((exp, expIndex) => (
                    <div key={expIndex} className="mb-6 p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-gray-900">Experience {expIndex + 1}</h3>
                        {experience.length > 1 && (
                          <button
                            onClick={() => handleRemoveExperience(expIndex)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="space-y-3">
                        <div className="grid md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              Company
                            </label>
                            <input
                              type="text"
                              value={exp.company}
                              onChange={e => {
                                const updated = [...experience];
                                updated[expIndex].company = e.target.value;
                                setExperience(updated);
                              }}
                              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none text-sm"
                              placeholder="Company Name"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              Job Title
                            </label>
                            <input
                              type="text"
                              value={exp.title}
                              onChange={e => {
                                const updated = [...experience];
                                updated[expIndex].title = e.target.value;
                                setExperience(updated);
                              }}
                              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none text-sm"
                              placeholder="Position Title"
                            />
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              Start Date
                            </label>
                            <input
                              type="month"
                              value={exp.startDate}
                              onChange={e => {
                                const updated = [...experience];
                                updated[expIndex].startDate = e.target.value;
                                setExperience(updated);
                              }}
                              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              End Date
                            </label>
                            <input
                              type="month"
                              value={exp.endDate}
                              onChange={e => {
                                const updated = [...experience];
                                updated[expIndex].endDate = e.target.value;
                                setExperience(updated);
                              }}
                              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none text-sm"
                              placeholder="Present"
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs font-semibold text-gray-700">
                              Key Achievements
                            </label>
                            <button
                              onClick={() => handleGenerateAIDescription(expIndex)}
                              disabled={generatingAI === expIndex}
                              className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg text-xs font-semibold hover:from-cyan-600 hover:to-blue-600 transition disabled:opacity-50 shadow-sm"
                            >
                              {generatingAI === expIndex ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                  <span>Generating...</span>
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-3 h-3" />
                                  <span>Generate with AI</span>
                                </>
                              )}
                            </button>
                          </div>
                          {exp.bullets.map((bullet, bulletIndex) => (
                            <div key={bulletIndex} className="flex gap-2 mb-2">
                              <input
                                type="text"
                                value={bullet}
                                onChange={e => {
                                  const updated = [...experience];
                                  updated[expIndex].bullets[bulletIndex] = e.target.value;
                                  setExperience(updated);
                                }}
                                className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none text-sm"
                                placeholder="Describe your achievement..."
                              />
                              {exp.bullets.length > 1 && (
                                <button
                                  onClick={() => handleRemoveBullet(expIndex, bulletIndex)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => handleAddBullet(expIndex)}
                            className="text-cyan-600 hover:text-cyan-700 font-semibold text-xs flex items-center gap-1 mt-1"
                          >
                            <Plus className="w-3 h-3" />
                            Add Achievement
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleAddExperience}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-cyan-500 hover:text-cyan-600 font-semibold transition text-sm flex items-center justify-center gap-2 mb-4"
                >
                  <Plus className="w-4 h-4" />
                  Add More Experience
                </button>
                <div className="flex justify-between">
                  <button
                    onClick={() => setStep(2)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition flex items-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    onClick={() => setStep(4)}
                    className="px-6 py-3 bg-cyan-500 text-white rounded-xl font-bold hover:bg-cyan-600 transition flex items-center gap-2"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Education</h2>
                {education.map((edu, eduIndex) => (
                  <div key={eduIndex} className="mb-4 p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-gray-900 text-sm">Education {eduIndex + 1}</h3>
                      {education.length > 1 && (
                        <button
                          onClick={() => handleRemoveEducation(eduIndex)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          School/University
                        </label>
                        <input
                          type="text"
                          value={edu.school}
                          onChange={e => {
                            const updated = [...education];
                            updated[eduIndex].school = e.target.value;
                            setEducation(updated);
                          }}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none text-sm"
                          placeholder="University Name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Degree
                        </label>
                        <input
                          type="text"
                          value={edu.degree}
                          onChange={e => {
                            const updated = [...education];
                            updated[eduIndex].degree = e.target.value;
                            setEducation(updated);
                          }}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none text-sm"
                          placeholder="Bachelor's, Master's, etc."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Field of Study
                        </label>
                        <input
                          type="text"
                          value={edu.field}
                          onChange={e => {
                            const updated = [...education];
                            updated[eduIndex].field = e.target.value;
                            setEducation(updated);
                          }}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none text-sm"
                          placeholder="Computer Science, etc."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Graduation Date
                        </label>
                        <input
                          type="month"
                          value={edu.graduationDate}
                          onChange={e => {
                            const updated = [...education];
                            updated[eduIndex].graduationDate = e.target.value;
                            setEducation(updated);
                          }}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  onClick={handleAddEducation}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-cyan-500 hover:text-cyan-600 font-semibold transition text-sm flex items-center justify-center gap-2 mb-4"
                >
                  <Plus className="w-4 h-4" />
                  Add More Education
                </button>
                <div className="flex justify-between">
                  <button
                    onClick={() => setStep(3)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition flex items-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    onClick={() => setStep(5)}
                    className="px-6 py-3 bg-cyan-500 text-white rounded-xl font-bold hover:bg-cyan-600 transition flex items-center gap-2"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {step === 5 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Skills & Certifications</h2>
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Skills
                  </label>
                  {skills.map((skill, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={skill}
                        onChange={e => {
                          const updated = [...skills];
                          updated[index] = e.target.value;
                          setSkills(updated);
                        }}
                        className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none text-sm"
                        placeholder="e.g., Project Management, Communication"
                      />
                      {skills.length > 1 && (
                        <button
                          onClick={() => handleRemoveSkill(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={handleAddSkill}
                    className="text-cyan-600 hover:text-cyan-700 font-semibold text-sm flex items-center gap-1 mt-2"
                  >
                    <Plus className="w-3 h-3" />
                    Add Skill
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Certifications (Optional)
                  </label>
                  {certifications.map((cert, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={cert}
                        onChange={e => {
                          const updated = [...certifications];
                          updated[index] = e.target.value;
                          setCertifications(updated);
                        }}
                        className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none text-sm"
                        placeholder="e.g., PMP Certified, Google Analytics"
                      />
                      {certifications.length > 1 && (
                        <button
                          onClick={() => {
                            setCertifications(certifications.filter((_, i) => i !== index));
                          }}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setCertifications([...certifications, ''])}
                    className="text-cyan-600 hover:text-cyan-700 font-semibold text-sm flex items-center gap-1 mt-2"
                  >
                    <Plus className="w-3 h-3" />
                    Add Certification
                  </button>
                </div>

                <div className="mt-6 flex justify-between">
                  <button
                    onClick={() => setStep(4)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition flex items-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    onClick={() => setStep(6)}
                    className="px-6 py-3 bg-cyan-500 text-white rounded-xl font-bold hover:bg-cyan-600 transition flex items-center gap-2"
                  >
                    Review & Generate
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {step === 6 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Review & Generate</h2>
                <div className="bg-cyan-50 border-2 border-cyan-300 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-cyan-900 mb-1">AI Will Enhance Your Resume</h3>
                      <ul className="text-cyan-800 space-y-1 text-sm">
                        <li>• Transform bullets into impactful statements</li>
                        <li>• Optimize for ATS systems</li>
                        <li>• Generate professional summary</li>
                        <li>• Format everything beautifully</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-gray-900 mb-1 text-sm">Personal Info</h4>
                    <p className="text-sm text-gray-700">
                      {personalInfo.fullName} • {personalInfo.email} • {personalInfo.phone}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-gray-900 mb-1 text-sm">Experience</h4>
                    <p className="text-sm text-gray-700">
                      {experience.filter(e => e.company && e.title).length} position(s)
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-gray-900 mb-1 text-sm">Education</h4>
                    <p className="text-sm text-gray-700">
                      {education.filter(e => e.school && e.degree).length} degree(s)
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-gray-900 mb-1 text-sm">Skills</h4>
                    <p className="text-sm text-gray-700">
                      {skills.filter(s => s.trim()).length} skill(s)
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl p-4 text-white mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold mb-1">Cost: 10 Credits</h3>
                      <p className="text-white/90 text-sm">
                        Balance after: {balance - 10} credits
                      </p>
                    </div>
                    <Coins className="w-10 h-10 opacity-80" />
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={() => setStep(5)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition flex items-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    onClick={handleGenerateResume}
                    disabled={generating || balance < 10}
                    className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
                  >
                    {generating ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Generate My Resume
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {showPreview && selectedTemplate && (
            <div className="sticky top-4 h-fit">
              <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="font-bold text-gray-900">Live Preview</h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleZoomOut}
                        disabled={zoomLevel <= 0.2}
                        className="p-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Zoom Out"
                      >
                        <ZoomOut className="w-4 h-4 text-gray-700" />
                      </button>
                      <span className="text-xs font-semibold text-gray-600 min-w-[3rem] text-center">
                        {Math.round(zoomLevel * 100)}%
                      </span>
                      <button
                        onClick={handleZoomIn}
                        disabled={zoomLevel >= 1.0}
                        className="p-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Zoom In"
                      >
                        <ZoomIn className="w-4 h-4 text-gray-700" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowFullPreview(true)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition"
                    title="Full Preview"
                  >
                    <Maximize2 className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-gray-50 relative">
                  {/* Magnifying Glass Overlay */}
                  <div className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-md">
                    <Search className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="transform origin-top-left" style={{
                    transform: `scale(${zoomLevel})`,
                    width: '816px',
                    height: '1056px'
                  }}>
                    <ResumeTemplateRenderer
                      template={selectedTemplate}
                      resumeData={getCurrentResumeData()}
                      scale={1}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showFullPreview && selectedTemplate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-gray-900">Full Preview</h2>
              <button
                onClick={() => setShowFullPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="p-6">
              <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                <div className="scale-[0.6] origin-top-left" style={{ width: '816px', height: '1056px' }}>
                  <ResumeTemplateRenderer
                    template={selectedTemplate}
                    resumeData={getCurrentResumeData()}
                    scale={1}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
